import { OrbindApiError } from '@orbind/api-client'
import { Button, FieldBlock, Input, Modal, Select, Textarea, useToast } from '@orbind/ui'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import { api } from '../lib/api.ts'
import { useBootstrap } from '../lib/catalog.ts'
import { useCatalogLabel, type PortfolioLevelCode } from './PortfolioExplorer.tsx'

/**
 * Cadastro e edicao de um item do portfólio.
 *
 * O protótipo mostra "Adicionar filho" e "Editar" como acoes do cabecalho
 * (`showPort.html:15-19`) sem sair da tela — por isso um modal, e nao uma rota
 * nova: sair do detalhe perderia o contexto da árvore ao lado.
 *
 * O nível nunca e escolhido a mao: ele e derivado do pai pela hierarquia fixa
 * Solução → Produto → Módulo → Funcionalidade → Ação.
 */

export type PortfolioNodeFormValue = {
  code: string
  name: string
  description: string
  portfolioStatusId: string | null
  ownerMemberId: string | null
  companyAreaId: string | null
}

const VAZIO: PortfolioNodeFormValue = {
  code: '',
  name: '',
  description: '',
  portfolioStatusId: null,
  ownerMemberId: null,
  companyAreaId: null,
}

export function PortfolioNodeForm({
  open,
  onClose,
  mode,
  levelLabel,
  level,
  parentId,
  parentName,
  nodeId,
  initial,
}: {
  open: boolean
  onClose: () => void
  mode: 'create' | 'edit'
  /** Rótulo traduzido do nível que está sendo criado/editado. */
  levelLabel: string
  /** Nível técnico — só usado na criação. */
  level?: PortfolioLevelCode
  parentId?: string | null
  parentName?: string | null
  nodeId?: string
  initial?: Partial<PortfolioNodeFormValue>
}) {
  const bootstrap = useBootstrap()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const toast = useToast()
  const label = useCatalogLabel()
  const [form, setForm] = useState<PortfolioNodeFormValue>({ ...VAZIO, ...initial })

  // Reabrir o modal precisa reapresentar os valores do nó atual, nao os do
  // ultimo que foi editado.
  useEffect(() => {
    if (open) setForm({ ...VAZIO, ...initial })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, nodeId, parentId])

  const set = <K extends keyof PortfolioNodeFormValue>(key: K, value: PortfolioNodeFormValue[K]) =>
    setForm((atual) => ({ ...atual, [key]: value }))

  const salvar = useMutation({
    mutationFn: async () => {
      const payload = {
        code: form.code.trim(),
        name: form.name.trim(),
        description: form.description.trim() === '' ? null : form.description.trim(),
        portfolioStatusId: form.portfolioStatusId,
        ownerMemberId: form.ownerMemberId,
        companyAreaId: form.companyAreaId,
      }
      if (mode === 'edit' && nodeId) {
        return (await api.portfolio[':id'].$patch({ param: { id: nodeId }, json: payload })).json()
      }
      if (!level) throw new Error('nivel do item do portfolio nao informado')
      return (await api.portfolio.$post({ json: { ...payload, level, parentId: parentId ?? null } })).json()
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['portfolio'] })
      toast.push({
        tone: 'success',
        message: mode === 'edit' ? `${levelLabel} atualizado.` : `${levelLabel} criado.`,
      })
      onClose()
      if (mode === 'create' && 'node' in data && data.node) navigate(`/portfolio/${data.node.id}`)
    },
    onError: (error) => {
      toast.push({
        tone: 'danger',
        message:
          error instanceof OrbindApiError ? error.message : 'Não foi possível salvar. Tente novamente.',
      })
    },
  })

  const invalido = form.code.trim() === '' || form.name.trim() === ''

  return (
    <Modal
      open={open}
      onClose={onClose}
      width={620}
      title={mode === 'edit' ? `Editar ${levelLabel.toLowerCase()}` : `Novo ${levelLabel.toLowerCase()}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="primary" disabled={invalido || salvar.isPending} onClick={() => salvar.mutate()}>
            {salvar.isPending ? 'Salvando…' : 'Salvar'}
          </Button>
        </>
      }
    >
      <div className="pt-fields">
        {mode === 'create' && parentName ? (
          <p className="pt-note">
            <i className="fas fa-info-circle" aria-hidden />
            <span>
              Será criado como <strong>{levelLabel}</strong> abaixo de <strong>{parentName}</strong>. A hierarquia
              Solução → Produto → Módulo → Funcionalidade → Ação é fixa.
            </span>
          </p>
        ) : null}

        <div className="pt-fields__pair">
          <FieldBlock label="Código" required hint="Único no portfólio inteiro.">
            {({ id }) => (
              <Input
                id={id}
                value={form.code}
                onChange={(e) => set('code', e.target.value)}
                placeholder="Ex.: SOL-FIN"
                invalid={form.code.trim() === ''}
              />
            )}
          </FieldBlock>
          <FieldBlock label="Situação">
            {({ id }) => (
              <Select
                id={id}
                value={form.portfolioStatusId}
                onChange={(value) => set('portfolioStatusId', value)}
                placeholder="Sem situação"
                options={(bootstrap.data?.portfolioStatuses ?? []).map((status) => ({
                  value: status.id,
                  label: label(status.nameTranslationKey, status.code),
                }))}
              />
            )}
          </FieldBlock>
        </div>

        <FieldBlock label="Nome" required>
          {({ id }) => (
            <Input
              id={id}
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              placeholder={`Nome do ${levelLabel.toLowerCase()}`}
              invalid={form.name.trim() === ''}
            />
          )}
        </FieldBlock>

        <FieldBlock label="Descrição">
          {({ id }) => (
            <Textarea
              id={id}
              rows={4}
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              placeholder="O que este item entrega e por que ele existe"
            />
          )}
        </FieldBlock>

        <div className="pt-fields__pair">
          <FieldBlock label="Responsável">
            {({ id }) => (
              <Select
                id={id}
                value={form.ownerMemberId}
                onChange={(value) => set('ownerMemberId', value)}
                placeholder="Sem responsável"
                options={(bootstrap.data?.members ?? []).map((person) => ({
                  value: person.id,
                  label: person.name,
                }))}
              />
            )}
          </FieldBlock>
          <FieldBlock label="Área">
            {({ id }) => (
              <Select
                id={id}
                value={form.companyAreaId}
                onChange={(value) => set('companyAreaId', value)}
                placeholder="Sem área"
                options={(bootstrap.data?.areas ?? []).map((area) => ({
                  value: area.id,
                  label: label(area.nameTranslationKey, area.code),
                }))}
              />
            )}
          </FieldBlock>
        </div>
      </div>
    </Modal>
  )
}
